import { claudeCodeInstance } from './instance';
import type {
  AgentAssetEvolutionCandidatesResponse,
  DashboardCostGovernanceResponse,
  DashboardOverviewResponse,
  DashboardRolloutHealthFilters,
  DashboardRolloutHealthResponse,
  AgentRoleAssetDetailResponse,
  AgentRoleAssetListFilters,
  AgentRoleAssetRollbackRequest,
  AgentRoleAssetsResponse,
  AgentRoleAssetTransitionRequest,
  DashboardTopListKind,
  DashboardTopListResponse,
  DashboardSessionResponse,
  EmployeeSessionsResponse,
  EmployeeSummaryResponse,
  GenerateSkillProposalsResponse,
  SessionEventsResponse,
  SkillProposalDetailResponse,
  SkillProposalReviewRequest,
  SkillProposalRow,
  SkillProposalsResponse,
} from './types';

export const exchangeGoogleIdToken = async (idToken: string): Promise<DashboardSessionResponse> => {
  const response = await claudeCodeInstance.post<DashboardSessionResponse>('/agent/dashboard/auth/google', {
    idToken,
  });
  return response.data;
};

export const fetchDashboardOverview = async (windowDays: number): Promise<DashboardOverviewResponse> => {
  const response = await claudeCodeInstance.get<DashboardOverviewResponse>('/agent/dashboard/overview', {
    params: { windowDays },
  });
  return response.data;
};

export const fetchDashboardCostGovernance = async (
  windowDays: number,
  limit: number,
): Promise<DashboardCostGovernanceResponse> => {
  const response = await claudeCodeInstance.get<DashboardCostGovernanceResponse>('/agent/dashboard/cost-governance', {
    params: { windowDays, limit },
  });
  return response.data;
};

export const fetchDashboardRolloutHealth = async (
  windowDays: number,
  filters: DashboardRolloutHealthFilters,
): Promise<DashboardRolloutHealthResponse> => {
  const response = await claudeCodeInstance.get<DashboardRolloutHealthResponse>('/agent/dashboard/rollout-health', {
    params: {
      windowDays,
      role: filters.role?.trim() || undefined,
      pluginVersion: filters.pluginVersion?.trim() || undefined,
      mcpStatus: filters.mcpStatus || undefined,
      sortBy: filters.sortBy || undefined,
      limit: filters.limit ?? 50,
      offset: filters.offset ?? 0,
    },
  });
  return response.data;
};

export const fetchDashboardTopList = async (
  kind: DashboardTopListKind,
  windowDays: number,
  limit: number,
  offset: number,
): Promise<DashboardTopListResponse> => {
  const response = await claudeCodeInstance.get<DashboardTopListResponse>(`/agent/dashboard/top/${kind}`, {
    params: { windowDays, limit, offset },
  });
  return response.data;
};

export const fetchAgentAssetEvolutionCandidates = async (
  windowDays: number,
  limit: number,
): Promise<AgentAssetEvolutionCandidatesResponse> => {
  const response = await claudeCodeInstance.get<AgentAssetEvolutionCandidatesResponse>(
    '/agent/dashboard/asset-evolution-candidates',
    {
      params: { windowDays, limit },
    },
  );
  return response.data;
};

export const fetchEmployeeSummary = async (
  employeeId: string,
  windowDays: number,
): Promise<EmployeeSummaryResponse> => {
  const response = await claudeCodeInstance.get<EmployeeSummaryResponse>(
    `/agent/dashboard/employees/${encodeURIComponent(employeeId)}/summary`,
    { params: { windowDays } },
  );
  return response.data;
};

export const fetchEmployeeSessions = async (
  employeeId: string,
  windowDays: number,
  limit: number,
  offset: number,
): Promise<EmployeeSessionsResponse> => {
  const response = await claudeCodeInstance.get<EmployeeSessionsResponse>(
    `/agent/dashboard/employees/${encodeURIComponent(employeeId)}/sessions`,
    { params: { windowDays, limit, offset } },
  );
  return response.data;
};

export const fetchSessionEvents = async (sessionId: string, limit: number): Promise<SessionEventsResponse> => {
  const response = await claudeCodeInstance.get<SessionEventsResponse>(
    `/agent/dashboard/sessions/${encodeURIComponent(sessionId)}/events`,
    { params: { limit } },
  );
  return response.data;
};

export const fetchSkillProposals = async (params: {
  status?: string;
  proposalSource?: string;
  employeeId?: string;
  role?: string;
  limit?: number;
}): Promise<SkillProposalsResponse> => {
  const response = await claudeCodeInstance.get<SkillProposalsResponse>('/agent/dashboard/skill-proposals', {
    params,
  });
  return response.data;
};

export const fetchSkillProposalDetail = async (proposalId: string): Promise<SkillProposalDetailResponse> => {
  const response = await claudeCodeInstance.get<SkillProposalDetailResponse>(
    `/agent/dashboard/skill-proposals/${encodeURIComponent(proposalId)}`,
  );
  return response.data;
};

export const generateSkillProposals = async (params: {
  employeeId?: string;
  rangeStart?: string;
  rangeEnd?: string;
}): Promise<GenerateSkillProposalsResponse> => {
  const response = await claudeCodeInstance.post<GenerateSkillProposalsResponse>(
    '/agent/dashboard/skill-proposals/generate',
    params,
  );
  return response.data;
};

export const reviewSkillProposal = async (params: SkillProposalReviewRequest): Promise<SkillProposalRow> => {
  const response = await claudeCodeInstance.patch<SkillProposalRow>(
    `/agent/dashboard/skill-proposals/${encodeURIComponent(params.proposalId)}/review`,
    {
      action: params.action,
      note: params.note,
      finalScope: params.finalScope,
      selectedRoles: params.selectedRoles,
    },
  );
  return response.data;
};

export const fetchAgentRoleAssets = async (filters: AgentRoleAssetListFilters): Promise<AgentRoleAssetsResponse> => {
  const response = await claudeCodeInstance.get<AgentRoleAssetsResponse>('/agent/dashboard/role-assets', {
    params: {
      kind: filters.kind || undefined,
      scope: filters.scope || undefined,
      status: filters.status || undefined,
      role: filters.role?.trim() || undefined,
      limit: filters.limit ?? 250,
    },
  });
  const adapterFilter = filters.adapter?.trim().toLowerCase();
  if (!adapterFilter) {
    return response.data;
  }
  return {
    assets: response.data.assets.filter((asset) => asset.adapter.toLowerCase().includes(adapterFilter)),
  };
};

export const fetchAgentRoleAssetDetail = async (assetId: string): Promise<AgentRoleAssetDetailResponse> => {
  const response = await claudeCodeInstance.get<AgentRoleAssetDetailResponse>(
    `/agent/dashboard/role-assets/${encodeURIComponent(assetId)}`,
  );
  return response.data;
};

export const publishAgentRoleAsset = async ({
  assetId,
  note,
}: AgentRoleAssetTransitionRequest): Promise<AgentRoleAssetDetailResponse> => {
  const response = await claudeCodeInstance.patch<AgentRoleAssetDetailResponse>(
    `/agent/dashboard/role-assets/${encodeURIComponent(assetId)}/publish`,
    { note },
  );
  return response.data;
};

export const retireAgentRoleAsset = async ({
  assetId,
  note,
}: AgentRoleAssetTransitionRequest): Promise<AgentRoleAssetDetailResponse> => {
  const response = await claudeCodeInstance.patch<AgentRoleAssetDetailResponse>(
    `/agent/dashboard/role-assets/${encodeURIComponent(assetId)}/retire`,
    { note },
  );
  return response.data;
};

export const rollbackAgentRoleAsset = async ({
  assetId,
  version,
  note,
}: AgentRoleAssetRollbackRequest): Promise<AgentRoleAssetDetailResponse> => {
  const response = await claudeCodeInstance.patch<AgentRoleAssetDetailResponse>(
    `/agent/dashboard/role-assets/${encodeURIComponent(assetId)}/rollback`,
    { version, note },
  );
  return response.data;
};
